// Dedicated "Add New Task" / "Edit Task" modal for the Task Tracker page
// only. It does not replace the shared CreateTaskForm used elsewhere in the
// app (Task Management, Sports/Music coach dashboards, etc.) — those keep
// their existing form untouched.
//
// Edit mode (mode="edit", with an initialTask) reuses the same layout but:
//  - loads the task's existing sub-tasks into the same editable table
//    create mode uses, so they can be renamed/rescheduled/re-prioritized
//    or removed, and new ones added — onSubmit gets a `subtaskChanges`
//    diff (added/updated/deletedIds) for the parent to apply via the
//    existing per-subtask add/update/delete endpoints (still one call per
//    subtask, sequentially — see TaskTracker.js's handleUpdateTask for why).
//  - "Completed" is only reachable once every sub-task on the task is
//    itself completed — mirrored by a server-side check in updateTask.
//  - makes Balgruha read-only, since the update endpoint doesn't accept a
//    balagruhaId change at all (it would be silently ignored server-side).
//  - shows the task's real assigned (created) date instead of "today".
import React, { useState } from 'react';
import {
    Plus,
    Pencil,
    X,
    AlertTriangle,
    AlertCircle,
    CheckCircle2,
    ListChecks,
    Calendar,
    Trash2,
} from 'lucide-react';
import './AddTaskModal.css';
import SearchableSelect from '../common/SearchableSelect';

const todayISODate = () => new Date().toISOString().slice(0, 10);

const formatDisplayDate = (isoDate) => {
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

let subtaskRowSeq = 0;
const makeEmptySubtaskRow = () => ({
    key: `new-subtask-${++subtaskRowSeq}`,
    title: '',
    dueDate: '',
    priority: 'medium',
});

const toISODateValue = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
};

function AddTaskModal({ balagruhas, users, defaultBalagruhaId, onSubmit, onCancel, mode = 'create', initialTask = null }) {
    const today = todayISODate();
    const isEditMode = mode === 'edit' && !!initialTask;

    const [balagruhaId, setBalagruhaId] = useState(defaultBalagruhaId || '');
    const [title, setTitle] = useState(initialTask?.title || '');
    const [assignedUser, setAssignedUser] = useState(
        initialTask ? (initialTask.assignedUser?._id || initialTask.assignedUser || '') : ''
    );
    const [dueDate, setDueDate] = useState(initialTask ? toISODateValue(initialTask.deadline) : '');
    // Drives the 3-card Urgent/Important/Completed selector. "Completed" is
    // only reachable in edit mode (a brand new task can't start out done),
    // and represents the task's status rather than its priority — handled
    // on submit below.
    const [priorityCardSelection, setPriorityCardSelection] = useState(() => {
        if (isEditMode && initialTask.status === 'completed') return 'completed';
        return initialTask?.priority === 'high' ? 'high' : 'medium';
    });
    const [description, setDescription] = useState(initialTask?.description || '');
    const [subtasks, setSubtasks] = useState(() => {
        if (isEditMode) {
            return (initialTask.subtasks || []).map((st) => ({
                key: st._id,
                id: st._id,
                title: st.title || '',
                dueDate: toISODateValue(st.dueDate),
                priority: ['low', 'medium', 'high'].includes(st.priority) ? st.priority : 'medium',
                status: st.status || 'pending',
            }));
        }
        return [makeEmptySubtaskRow()];
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // A brand new row (added via "Add Another Sub-Task" while editing) has
    // no id/status yet, so it always counts as incomplete once it has a
    // title — same as an existing sub-task that isn't marked completed.
    const hasIncompleteSubtasks = subtasks.some(
        (row) => row.title.trim() && row.status !== 'completed'
    );

    const assignedBalagruhaName = isEditMode
        ? ((balagruhas || []).find((b) => {
            const taskBalagruhaId = typeof initialTask.balagruhaId === 'object'
                ? (initialTask.balagruhaId?._id || '')
                : initialTask.balagruhaId;
            return String(b._id) === String(taskBalagruhaId);
        })?.name || 'N/A')
        : '';

    const updateSubtaskField = (key, field, value) => {
        setSubtasks((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
    };

    // The priority dropdown doubles as a status control for existing
    // sub-tasks (see the "Completed" option, only offered once a sub-task
    // has an id): picking it marks the sub-task completed without touching
    // its priority, while picking Urgent/Important sets the priority and
    // reopens the sub-task if it had been completed.
    const updateSubtaskPriorityOrStatus = (key, value) => {
        setSubtasks((prev) => prev.map((row) => {
            if (row.key !== key) return row;
            if (value === 'completed') return { ...row, status: 'completed' };
            return { ...row, priority: value, status: row.status === 'completed' ? 'pending' : row.status };
        }));
        // Completing a sub-task may resolve the "complete every sub-task
        // first" warning shown on the disabled Completed card — drop the
        // stale message rather than leaving it displayed once it no longer
        // applies.
        setErrors((prev) => {
            if (!prev.priority) return prev;
            const { priority, ...rest } = prev;
            return rest;
        });
    };

    const addSubtaskRow = () => {
        setSubtasks((prev) => [...prev, makeEmptySubtaskRow()]);
    };

    const removeSubtaskRow = (key) => {
        setSubtasks((prev) => prev.filter((row) => row.key !== key));
    };

    // Editing a task without touching its due date: the date isn't
    // re-checked or re-sent, so an overdue task can still be edited (e.g.
    // marked Completed) without "Invalid or past deadline".
    const dueDateUnchanged = isEditMode && dueDate === toISODateValue(initialTask.deadline);

    const validate = () => {
        const nextErrors = {};
        if (!isEditMode && !balagruhaId) nextErrors.balagruhaId = 'Balgruha is required';
        if (!title.trim()) nextErrors.title = 'Task name is required';
        if (!assignedUser) nextErrors.assignedUser = 'Assignee is required';
        if (!description.trim()) nextErrors.description = 'Description is required';
        if (!dueDate) {
            nextErrors.dueDate = 'Due date is required';
        } else if (dueDate < today && !dueDateUnchanged) {
            nextErrors.dueDate = 'Due date cannot be in the past';
        }

        subtasks.forEach((row) => {
            if (row.title.trim() && !row.dueDate) {
                nextErrors[`subtask-${row.key}`] = 'Due date is required for this sub-task';
            }
        });

        // Belt-and-braces: the Completed card is only clickable in edit
        // mode once nothing's incomplete, but re-check here too in case a
        // sub-task got added/edited after "Completed" was already picked.
        if (priorityCardSelection === 'completed' && hasIncompleteSubtasks) {
            nextErrors.priority = 'Complete all sub-tasks before marking this task completed';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (submitting) return;
        if (!validate()) return;

        setSubmitting(true);
        try {
            if (isEditMode) {
                // The generic update endpoint only understands these
                // top-level fields — no balagruhaId or subtasks. Picking
                // "Completed" sets status instead of priority; picking
                // Urgent/Important sets priority, and reopens the task
                // (back to "pending") if it had been marked completed.
                const payload = {
                    title: title.trim(),
                    description: description.trim(),
                    assignedUser,
                };
                if (!dueDateUnchanged) payload.deadline = dueDate;
                if (priorityCardSelection === 'completed') {
                    payload.status = 'completed';
                } else {
                    payload.priority = priorityCardSelection;
                    if (initialTask.status === 'completed') {
                        payload.status = 'pending';
                    }
                }

                // Auto-complete the task once every one of its sub-tasks is
                // completed, even if Urgent/Important was still selected —
                // mirrors the automatic completion used for the direct
                // sub-task toggle in the Task Tracker table.
                const namedSubtaskRows = subtasks.filter((row) => row.title.trim());
                if (namedSubtaskRows.length > 0 && namedSubtaskRows.every((row) => row.status === 'completed')) {
                    payload.status = 'completed';
                    delete payload.priority;
                }

                const initialSubtasksById = new Map(
                    (initialTask.subtasks || []).map((st) => [st._id, st])
                );
                const currentIds = new Set(subtasks.filter((row) => row.id).map((row) => row.id));
                payload.subtaskChanges = {
                    added: subtasks
                        .filter((row) => !row.id && row.title.trim())
                        .map((row) => ({
                            title: row.title.trim(),
                            dueDate: row.dueDate,
                            priority: row.priority,
                        })),
                    updated: subtasks
                        .filter((row) => {
                            if (!row.id) return false;
                            const original = initialSubtasksById.get(row.id);
                            if (!original) return false;
                            return (
                                row.title.trim() !== (original.title || '') ||
                                row.dueDate !== toISODateValue(original.dueDate) ||
                                row.priority !== (original.priority || 'medium') ||
                                row.status !== (original.status || 'pending')
                            );
                        })
                        .map((row) => ({
                            id: row.id,
                            title: row.title.trim(),
                            dueDate: row.dueDate,
                            priority: row.priority,
                            status: row.status,
                        })),
                    deletedIds: [...initialSubtasksById.keys()].filter((id) => !currentIds.has(id)),
                };

                await onSubmit(payload);
            } else {
                await onSubmit({
                    title: title.trim(),
                    description: description.trim(),
                    assignedUser,
                    deadline: dueDate,
                    priority: priorityCardSelection,
                    status: 'pending',
                    balagruhaId,
                    attachments: [],
                    drillOrExerciseType: '',
                    subtasks: subtasks
                        .filter((row) => row.title.trim())
                        .map((row) => ({
                            title: row.title.trim(),
                            dueDate: row.dueDate,
                            priority: row.priority,
                        })),
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="add-task-modal-overlay">
            <div className="add-task-modal">
                <div className="add-task-modal-header">
                    <div className="add-task-modal-header-left">
                        <span className="add-task-modal-icon" aria-hidden="true">
                            {isEditMode ? <Pencil size={16} strokeWidth={2.5} /> : <Plus size={18} strokeWidth={2.5} />}
                        </span>
                        <h3>{isEditMode ? 'Edit Task' : 'Add New Task'}</h3>
                    </div>
                    <button type="button" className="add-task-modal-close" onClick={onCancel} aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                <div className="add-task-modal-body">
                    <div className="add-task-form-row two-col">
                        <div className="add-task-field">
                            <label htmlFor="add-task-balagruha">Balgruha <span className="add-task-required">*</span></label>
                            {isEditMode ? (
                                <div className="add-task-readonly-date" title="Balgruha can't be changed once a task is created">
                                    {assignedBalagruhaName}
                                </div>
                            ) : (
                                <>
                                    <SearchableSelect
                                        searchPlaceholder="Search balagruha..."
                                        id="add-task-balagruha"
                                        value={balagruhaId}
                                        onChange={(e) => setBalagruhaId(e.target.value)}
                                        className={errors.balagruhaId ? 'has-error' : ''}
                                    >
                                        <option value="">Select Balgruha</option>
                                        {(balagruhas || []).map((b) => (
                                            <option key={b._id} value={b._id}>{b.name}</option>
                                        ))}
                                    </SearchableSelect>
                                    {errors.balagruhaId && <div className="add-task-field-error">{errors.balagruhaId}</div>}
                                </>
                            )}
                        </div>
                        <div className="add-task-field">
                            <label htmlFor="add-task-title">Task Name <span className="add-task-required">*</span></label>
                            <input
                                id="add-task-title"
                                type="text"
                                placeholder="Enter task name"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className={errors.title ? 'has-error' : ''}
                            />
                            {errors.title && <div className="add-task-field-error">{errors.title}</div>}
                        </div>
                    </div>

                    <div className="add-task-form-row three-col">
                        <div className="add-task-field">
                            <label htmlFor="add-task-assignee">Assignee <span className="add-task-required">*</span></label>
                            <SearchableSelect
                                searchPlaceholder="Search coach..."
                                id="add-task-assignee"
                                value={assignedUser}
                                onChange={(e) => setAssignedUser(e.target.value)}
                                className={errors.assignedUser ? 'has-error' : ''}
                            >
                                <option value="">Select coach</option>
                                {(users || []).map((u) => (
                                    <option key={u._id} value={u._id}>{u.name}</option>
                                ))}
                            </SearchableSelect>
                            {errors.assignedUser && <div className="add-task-field-error">{errors.assignedUser}</div>}
                        </div>
                        <div className="add-task-field">
                            <label>Assigned Date <span className="add-task-required">*</span></label>
                            <div className="add-task-readonly-date">
                                <Calendar size={14} /> {formatDisplayDate(isEditMode ? initialTask.createdAt : today)}
                            </div>
                        </div>
                        <div className="add-task-field">
                            <label htmlFor="add-task-due-date">Due Date <span className="add-task-required">*</span></label>
                            <input
                                id="add-task-due-date"
                                type="date"
                                min={today}
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                className={errors.dueDate ? 'has-error' : ''}
                            />
                            {errors.dueDate && <div className="add-task-field-error">{errors.dueDate}</div>}
                        </div>
                    </div>

                    <div className="add-task-field">
                        <label>Priority <span className="add-task-required">*</span></label>
                        <div className="add-task-priority-cards">
                            <button
                                type="button"
                                className={`add-task-priority-card urgent ${priorityCardSelection === 'high' ? 'selected' : ''}`}
                                onClick={() => setPriorityCardSelection('high')}
                            >
                                <AlertTriangle size={18} />
                                <div>
                                    <div className="add-task-priority-title">Urgent</div>
                                    <div className="add-task-priority-desc">Needs immediate attention</div>
                                </div>
                            </button>
                            <button
                                type="button"
                                className={`add-task-priority-card important ${priorityCardSelection === 'medium' ? 'selected' : ''}`}
                                onClick={() => setPriorityCardSelection('medium')}
                            >
                                <AlertCircle size={18} />
                                <div>
                                    <div className="add-task-priority-title">Important</div>
                                    <div className="add-task-priority-desc">Plan and complete soon</div>
                                </div>
                            </button>
                            {isEditMode ? (
                                hasIncompleteSubtasks ? (
                                    <div
                                        className="add-task-priority-card completed disabled"
                                        title="Complete every sub-task first"
                                        onClick={() => setErrors((prev) => ({
                                            ...prev,
                                            priority: 'Complete all sub-tasks before marking this task completed',
                                        }))}
                                    >
                                        <CheckCircle2 size={18} />
                                        <div>
                                            <div className="add-task-priority-title">Completed</div>
                                            <div className="add-task-priority-desc">Complete all sub-tasks first</div>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className={`add-task-priority-card completed ${priorityCardSelection === 'completed' ? 'selected' : ''}`}
                                        onClick={() => setPriorityCardSelection('completed')}
                                    >
                                        <CheckCircle2 size={18} />
                                        <div>
                                            <div className="add-task-priority-title">Completed</div>
                                            <div className="add-task-priority-desc">Mark this task as done</div>
                                        </div>
                                    </button>
                                )
                            ) : (
                                <div
                                    className="add-task-priority-card completed disabled"
                                    title="A new task can't start out already completed"
                                >
                                    <CheckCircle2 size={18} />
                                    <div>
                                        <div className="add-task-priority-title">Completed</div>
                                        <div className="add-task-priority-desc">Available once the task exists</div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {errors.priority && <div className="add-task-field-error">{errors.priority}</div>}
                    </div>

                    <div className="add-task-field">
                        <label htmlFor="add-task-description">Description / Instructions <span className="add-task-required">*</span></label>
                        <textarea
                            id="add-task-description"
                            placeholder="Enter task details, instructions or any notes..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className={errors.description ? 'has-error' : ''}
                        />
                        {errors.description && <div className="add-task-field-error">{errors.description}</div>}
                    </div>

                    <div className="add-task-subtasks">
                        <div className="add-task-subtasks-header">
                            <span className="add-task-subtasks-icon" aria-hidden="true"><ListChecks size={16} /></span>
                            <div>
                                <div className="add-task-subtasks-title">Sub-Tasks</div>
                                <div className="add-task-subtasks-desc">
                                    Add the steps to complete this task. The main task will be marked completed only when all sub-tasks are completed.
                                </div>
                            </div>
                        </div>

                        <div className="add-task-subtasks-table-wrapper">
                            <table className="add-task-subtasks-table">
                                <thead>
                                    <tr>
                                        <th>Sr No</th>
                                        <th>Sub-Task Name <span className="add-task-required">*</span></th>
                                        <th>Due Date <span className="add-task-required">*</span></th>
                                        <th>Priority <span className="add-task-required">*</span></th>
                                        <th aria-hidden="true"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subtasks.map((row, index) => (
                                        <React.Fragment key={row.key}>
                                            <tr>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className="add-task-subtask-name-cell">
                                                        <input
                                                            type="text"
                                                            placeholder="Sub-task name"
                                                            value={row.title}
                                                            onChange={(e) => updateSubtaskField(row.key, 'title', e.target.value)}
                                                        />
                                                        {row.status === 'completed' && (
                                                            <span className="add-task-subtask-done-badge">Done</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        type="date"
                                                        min={today}
                                                        value={row.dueDate}
                                                        onChange={(e) => updateSubtaskField(row.key, 'dueDate', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <select
                                                        value={row.status === 'completed' ? 'completed' : row.priority}
                                                        onChange={(e) => updateSubtaskPriorityOrStatus(row.key, e.target.value)}
                                                        className={`add-task-subtask-priority-select ${row.status === 'completed' ? 'completed' : row.priority}`}
                                                    >
                                                        <option value="high">Urgent</option>
                                                        <option value="medium">Important</option>
                                                        {row.id && <option value="completed">Completed</option>}
                                                    </select>
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="add-task-subtask-remove"
                                                        onClick={() => removeSubtaskRow(row.key)}
                                                        aria-label="Remove sub-task"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                            {errors[`subtask-${row.key}`] && (
                                                <tr className="add-task-subtask-error-row">
                                                    <td colSpan={5}>{errors[`subtask-${row.key}`]}</td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <button type="button" className="add-task-add-subtask-btn" onClick={addSubtaskRow}>
                            <Plus size={14} /> Add Another Sub-Task
                        </button>
                    </div>
                </div>

                <div className="add-task-modal-footer">
                    <button type="button" className="add-task-btn-cancel" onClick={onCancel} disabled={submitting}>
                        Cancel
                    </button>
                    <button type="button" className="add-task-btn-submit" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? (isEditMode ? 'Saving…' : 'Creating…') : (isEditMode ? 'Save Changes' : 'Create Task')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AddTaskModal;
